import { useCallback, useEffect, useState } from 'react';
import { AuthDialog } from './components/AuthDialog';
import { AppShell, type ViewKey } from './components/AppShell';
import { useAuth } from './context/AuthContext';
import { AuditPage } from './pages/AuditPage';
import { AuctionsPage } from './pages/AuctionsPage';
import { CommissionsPage } from './pages/CommissionsPage';
import { DashboardPage } from './pages/DashboardPage';
import { CenterPage } from './pages/CenterPage';
import { MarketPage } from './pages/MarketPage';
import { MembersPage } from './pages/MembersPage';
import { NoticesPage } from './pages/NoticesPage';
import { BoardPage } from './pages/BoardPage';
import { ChatPage } from './pages/ChatPage';
import { ProfilePage } from './pages/ProfilePage';
import { RedeemPage } from './pages/RedeemPage';
import { SettingsPage } from './pages/SettingsPage';
import { getAppShareInfo, isNative, setupNativeShell } from './native';

const validViews: ViewKey[] = ['dashboard', 'center', 'commissions', 'market', 'auctions', 'notices', 'board', 'chat', 'members', 'settings', 'redeem', 'audit', 'profile'];
const staffViews: ViewKey[] = ['members', 'settings', 'redeem', 'audit'];

function currentView(): ViewKey {
  const hash = window.location.hash.replace(/^#\/?/, '') as ViewKey;
  return validViews.includes(hash) ? hash : 'dashboard';
}

function viewFromLink(link: string): ViewKey | null {
  const target = link.replace(/^#\/?/, '').split('?')[0] as ViewKey;
  return validViews.includes(target) ? target : null;
}

export function App() {
  const { permissions, loading, settings } = useAuth();
  const [view, setView] = useState<ViewKey>(currentView);
  const [authOpen, setAuthOpen] = useState(false);
  const appName = isNative ? getAppShareInfo(settings.siteName).name : settings.siteName;

  const navigate = useCallback((next: ViewKey) => {
    window.location.hash = '/' + next;
    setView(next);
  }, []);

  useEffect(() => {
    const onHashChange = () => setView(currentView());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    document.title = appName;
  }, [appName]);

  useEffect(() => {
    if (!loading && staffViews.includes(view) && !permissions.isStaff) {
      navigate('dashboard');
    }
  }, [loading, view, permissions.isStaff, navigate]);

  useEffect(() => setupNativeShell(link => {
    const target = viewFromLink(link);
    if (target) navigate(target);
  }), [navigate]);

  if (loading) {
    return <div className="boot-screen"><span className="brand-mark"><ShieldMark /></span><strong>{appName}</strong><small>正在建立安全会话…</small></div>;
  }

  const page = view === 'dashboard' ? <DashboardPage onNavigate={key => navigate(key)} /> :
    view === 'center' ? <CenterPage onNeedAuth={() => setAuthOpen(true)} /> :
    view === 'commissions' ? <CommissionsPage onNeedAuth={() => setAuthOpen(true)} /> :
    view === 'market' ? <CenterPage onNeedAuth={() => setAuthOpen(true)} /> :
    view === 'auctions' ? <AuctionsPage onNeedAuth={() => setAuthOpen(true)} /> :
    view === 'notices' ? <NoticesPage onNeedAuth={() => setAuthOpen(true)} /> :
    view === 'board' ? <BoardPage onNeedAuth={() => setAuthOpen(true)} /> :
    view === 'chat' ? <ChatPage onNeedAuth={() => setAuthOpen(true)} /> :
    view === 'members' ? <MembersPage /> :
    view === 'settings' ? <SettingsPage /> :
    view === 'redeem' ? <RedeemPage onNeedAuth={() => setAuthOpen(true)} /> :
    view === 'audit' ? <AuditPage /> :
    <ProfilePage onNeedAuth={() => setAuthOpen(true)} onNavigate={navigate} />;

  return (
    <>
      <AppShell view={view} onNavigate={navigate} onOpenAuth={() => setAuthOpen(true)}>{page}</AppShell>
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
}

function ShieldMark() {
  return <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3 4.5 6v5.4c0 4.5 3.1 8.3 7.5 9.6 4.4-1.3 7.5-5.1 7.5-9.6V6L12 3Z" /><path d="m9 12 2 2 4-4" /></svg>;
}

